/**
 * Integrytas Group - Common Security Module (CSM)
 * module_icons — resolves a per-tab / per-page glyph for the workspace chrome.
 *
 * Direction D gives every workspace tab a small module icon. BaseApp is
 * application-agnostic, so the icon is chosen by a generic keyword HEURISTIC
 * over the page's display label and internal code — never by an app-specific
 * page list. The first matching rule wins, so more specific tokens precede
 * broader ones (e.g. "provision" before "user"); anything unmatched falls back
 * to a neutral screen glyph. A derived app inherits this unchanged.
 */

import type { LucideIcon } from 'lucide-react';
import {
  Users, ShieldCheck, ShieldAlert, Building2, SlidersHorizontal, GitBranch,
  Bell, Database, FileText, LayoutGrid, Workflow, BookOpen, Languages,
  KeyRound, Boxes, Search, Gauge, Layers, Mail,
} from 'lucide-react';

// Ordered keyword → icon table. Matched against the lowercased "label code"
// string. Order matters: specific tokens first.
const ICON_RULES: readonly (readonly [RegExp, LucideIcon])[] = [
  [/provision/,                                    GitBranch],
  [/role|permission|rbac|\baccess\b/,              ShieldCheck],
  [/quality|breach|assert|integrity|privacy|dpc/,  ShieldAlert],
  [/compan|organi|division|business.?unit|office|party|supplier|customer|carrier/, Building2],
  [/parameter|setting|config|system/,              SlidersHorizontal],
  [/user|people|account|\bmember/,                 Users],
  [/approv|sign.?off|workflow|delegation/,         Workflow],
  [/notif|\balert|message|inbox/,                  Bell],
  [/mail|email|campaign|distribut|unsubscribe/,    Mail],
  [/locale|language|translat|i18n/,                Languages],
  [/secret|token|\bkey\b|credential|licen/,        KeyRound],
  [/help|\bdoc|manual|guide|content|cms/,          BookOpen],
  [/search|index|lookup/,                          Search],
  [/perf|telemetry|metric|monitor|guardrail|rate.?limit|circuit/, Gauge],
  [/data|table|record|dataset|import|export|archive/, Database],
  [/module|catalog|registry|feature.?flag/,        Boxes],
  [/page|screen|layout|menu/,                      Layers],
  [/audit|\blog|trail|history/,                     FileText],
];

const DEFAULT_ICON: LucideIcon = LayoutGrid;

/** Best-fit lucide icon for a page, by display label first then internal code. */
export function moduleIconFor(code: string, label?: string): LucideIcon {
  const hay = `${label ?? ''} ${code}`.toLowerCase();
  for (const [re, Icon] of ICON_RULES) {
    if (re.test(hay)) return Icon;
  }
  return DEFAULT_ICON;
}
